import { useEffect, useState } from "react";
import {
  Col,
  Row,
  Typography,
  Card,
  Form,
  Input,
  Select,
  Button,
  Alert,
  message,
  Skeleton,
  Divider,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPollH } from "@fortawesome/free-solid-svg-icons";
import {
  assignQuestionnaire,
  getCompanyEmployees,
} from "../repository/data.repository";
import { EmployeeUser } from "../repository/data.default";
import { useTokenInfo } from "../hooks/useTokenInfo";
import { getProblemMessage } from "../utils/http";

interface Employee {
  readonly uid: string;
  readonly label: string;
}

// PD-02 §6.16.6 — Send Survey. Reuses the Questionnaires taking stack
// (surveys are modeled as a Questionnaire type on the backend) and drops a
// draft answer per selected employee so the survey lands on their My Tasks.
function SendSurveyPage() {
  const { claims } = useTokenInfo();
  const companyUid = claims?.usrCmpUid;
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCompanyEmployees({
      searchQuery: { conditions: [] },
      pageNumber: 0,
      pageSize: 500,
    })
      .then((data) => {
        if (cancelled) return;
        setEmployees(
          (data.data ?? []).map((e: EmployeeUser) => ({
            uid: e.uid,
            label:
              [e.firstName, e.lastName].filter(Boolean).join(" ") ||
              e.userName ||
              e.uid,
          })),
        );
      })
      .catch(() => setEmployees([]))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(values: {
    questionnaireUid: string;
    userUids: string[];
  }) {
    if (!values.userUids?.length) return;
    setSubmitting(true);
    const results = await Promise.allSettled(
      values.userUids.map((uid) =>
        assignQuestionnaire({
          userUid: uid,
          questionnaireUid: values.questionnaireUid,
          companyUid,
        }),
      ),
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const bad = results.length - ok;
    if (bad === 0) {
      message.success(`Survey sent to ${ok} employee(s).`);
    } else if (ok === 0) {
      const first = results.find((r) => r.status === "rejected") as
        | PromiseRejectedResult
        | undefined;
      message.error(
        getProblemMessage(first?.reason) || "Failed to send survey.",
      );
    } else {
      message.warning(
        `Survey sent to ${ok}; ${bad} failed (may already have a draft).`,
      );
    }
    form.resetFields(["userUids"]);
    setSubmitting(false);
  }

  return (
    <Col className="animate__animated animate__fadeIn flex flex-auto flex-col gap-4 overflow-y-auto p-6">
      <Row>
        <Typography.Title level={3} className="!m-0">
          <FontAwesomeIcon icon={faPollH} className="mr-2" />
          Send Survey
        </Typography.Title>
      </Row>

      <Alert
        type="info"
        showIcon
        message="Send an existing Admin-published survey to selected employees. Paste its UID (shared by Admin), pick the recipients, and each gets a draft on their My Tasks."
      />

      <Card className="emp-card">
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            name="questionnaireUid"
            label="Survey UID"
            rules={[{ required: true, message: "Paste the survey UID" }]}
          >
            <Input placeholder="e.g. 7b1f1a0c-… (ask Admin for the UID)" />
          </Form.Item>
          <Divider />
          <Form.Item
            name="userUids"
            label="Employees"
            rules={[
              {
                required: true,
                message: "Pick at least one employee",
                type: "array",
                min: 1,
              },
            ]}
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 2 }} />
            ) : (
              <Select
                mode="multiple"
                showSearch
                placeholder="Pick employees"
                optionFilterProp="label"
                options={employees.map((e) => ({
                  value: e.uid,
                  label: e.label,
                }))}
                maxTagCount="responsive"
              />
            )}
          </Form.Item>
          <Row className="justify-end">
            <Button type="primary" htmlType="submit" loading={submitting}>
              Send survey
            </Button>
          </Row>
        </Form>
      </Card>
    </Col>
  );
}

export default SendSurveyPage;
